import ShoppingCart from '@c/checkout-v3/CartSlideOutV3';
import useDebounce from '@util/hooks/useDebounce';
import { Cart, CartItem, cartSchema } from '@util/types/firestore/carts';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import { useStoreSales } from './StoreSaleContext';
import { useToastContext } from './ToastContext';
import { ProductDocument } from '@util/types/firestore/products';
import ItemAddedToast from '@c/toasts/ItemAdded';
import useRealTimeProducts from '@util/hooks/useRealtimeProducts';
import isEqual from 'deep-eql';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getCartState, setCartState } from '@util/firestore/cart';
import { AddressDocument } from '@util/types/firestore/address';
import { europeCodes, southAmCodes } from '@util/types/locations';
import { Rate } from '@util/firestore/shipengine';
import { calculateTaxBatch } from '@util/firestore/taxjar';
import { Coupon } from '@util/firestore/stripe';
import ErrorToast from '@c/toasts/ErrorToast';
import { getProductById } from '@util/firestore/products';
import { logError } from '@util/logError';
import { formatCartItems, getTieCartId, getUserDataForDL, logAddToCart, pushToRevRollLayer, trackEvent } from '@util/analytics';
import {
  CART_TELEMETRY_EVENTS,
  CART_TELEMETRY_FINGERPRINTS,
  buildCartCommonContext,
  detectFocusClobber,
  detectRawCartDesync,
  emitCartTelemetry,
} from '@util/cartTelemetry';

export const storeSaleStartedNotice = (item: string) =>
  `A store sale started, so ${item} now costs less. Please review your cart.`;
export const storeSaleEndedNotice = (item: string, listPrice: string) =>
  `The store sale on ${item} has ended, so its price went back to ${listPrice}. Please review your cart.`;
export const STORE_SALE_COPY_C6A = 'The store sale on {item} changed, so its price is now {new price}. Please review your cart.';
export const STORE_SALE_COPY_C7A = 'The price of {item} changed, so it now costs {new price}. Please review your cart.';
export const STORE_SALE_COPY_C6B = 'Prices changed on {N} items in your cart. Please review your cart before you check out.';
export const STORE_SALE_COPY_C8A = 'The price of {item} changed. Your cart is updated. Please review it and try again.';
export const storeSalePriceChangedError = (item: string, cause: 'sale' | 'price' = 'sale') =>
  cause === 'price' ? STORE_SALE_COPY_C8A.replace('{item}', () => item)
    : `The price of ${item} changed because a store sale started or ended. Your cart is updated. Please review it and try again.`;

type ShoppingCartProviderProps = {
  children: ReactNode;
};

/**
 * Outcome of an increaseCartQty call, so callers (e.g. the drawer's undo)
 * can react correctly:
 *   'added'   — the item was added or its qty bumped.
 *   'blocked' — surfaced to the user (out of stock / stock cap / deleted).
 *   'dropped' — nothing happened and nothing was surfaced: either a concurrent
 *               add for the same product was already in flight, or the item was
 *               removed / the cart cleared during the fetch so a bump must NOT
 *               resurrect it.
 */
export type AddToCartResult = 'added' | 'blocked' | 'dropped';
type ProductId = string;
type ProductIdToRateMap = Map<ProductId, Rate>;
type SellerId = string;
type SelectedShippingRates = Map<SellerId, ProductIdToRateMap>;
type ShoppingCartContext = {
  addCoupons: (coupons: Coupon[]) => void;
  /**
   * Add a product to the guest (localStorage) cart. `stock`, when supplied,
   * caps the quantity client-side so a guest cannot add more than are
   * available (the server's validateOrder still backstops at payment). Callers
   * hold the full ProductDocument, so they pass `product.stock`.
   */
  addGuestCartItem: (item: CartItem, stock?: number) => AddToCartResult;
  canAddProductToCart: (productId: string) => boolean;
  cart: Cart;
  cartOpen: boolean;
  clearCart: () => void;
  coupons: Coupon[];
  decreaseCartQty: (id: string) => void;
  /**
   * Force-persist the current cart to Firestore, bypassing the 3s debounce.
   * Call this when the user transitions to checkout so the persisted cart
   * matches React state across devices/tabs. Resolves once the write
   * completes; resolves immediately for guests.
   */
  flushCart: () => Promise<void>;
  getDiscount: (total: number) => number;
  getItemCount: () => number;
  getItemQty: (id: string) => number;
  handleShippingRateSelected: (
    sellerId: string,
    productId: string,
    rate: Rate
  ) => void;
  increaseCartQty: (
    item: CartItem,
    dontOpenCart?: boolean,
    /**
     * Available stock for the item, used ONLY on the guest branch to cap the
     * client-side bump (guests do no fetch). Auth derives stock from the
     * add-time fetch / realtime. The drawer stepper passes its realtime
     * product's stock.
     */
    stock?: number
  ) => Promise<AddToCartResult>;
  /**
   * Product ids with an increaseCartQty currently in flight. Reactive (state,
   * not the internal ref) so surfaces can disable destructive actions on an
   * item mid-add — e.g. the drawer disables X-remove and Save-for-Later for
   * these ids so a remove can't race a resurrecting bump (GB #1323 F1).
   */
  addingProductIds: Set<string>;
  realTimeProducts: ProductDocument[];
  /** Shown when a stored store-sale cart price changes at a sale boundary. */
  storeSalePriceNotice: string | null;
  removeCoupon: (coupon: Coupon) => void;
  removeFromCart: (id: string) => void;
  selectedShippingRates: SelectedShippingRates;
  setCartOpen: React.Dispatch<React.SetStateAction<boolean>>;
  shippingSelectionComplete: boolean;
  taxInfo: {
    value?: number;
    itemValues?: Map<string, number>;
    isCalculating: boolean;
  };
  updateShippingAddress: (address: AddressDocument) => void;
  calculateTax: (items: CartItem[], shippingAddress: AddressDocument) => void;
  shippingAddress?: AddressDocument;
  /** V3: Move item from cart to saved-for-later list */
  saveForLater: (productId: string) => void;
  /** V3: Items saved for later */
  savedForLater: CartItem[];
  /**
   * True when every flat-rate item in the cart has a resolved shipping cost
   * (product data found AND a matching rate returned). A resolved $0 means
   * "genuinely free." False means at least one item is in one of three
   * non-resolved states: still being fetched (transient), has no shipping rate
   * for the buyer's region (region-unavailable), or its product was
   * deleted/unpublished (item-not-found). Distinguish them via
   * `flatRateShippingUnavailable` and `flatRateShippingNotFound`.
   * When false, the displayed/charged total is not yet final — block checkout.
   */
  flatRateShippingResolved: boolean;
  /**
   * Set when a flat-rate item's product IS available but `calculateShipping`
   * returns no rate for the buyer's region — i.e. the seller does not ship
   * there. This is a PERMANENT condition (unlike a not-yet-loaded product).
   * undefined means no such item (either resolved, or merely still loading).
   * Carries the buyer's city/state so the UI can show an actionable message.
   */
  flatRateShippingUnavailable?: {
    city?: string;
    state?: string;
  };
  /**
   * True when a flat-rate item's product was deleted/unpublished while in the
   * cart (the fallback fetch completed and returned null). HARD, non-transient
   * state — the UI must show an actionable "item no longer available" message
   * and checkout must be blocked (fail closed). Takes precedence over the
   * transient "Calculating…" state so the buyer never sees a stuck spinner.
   */
  flatRateShippingNotFound: boolean;
};

const ShoppingCartContext = createContext({} as ShoppingCartContext);

/**
 * Buyer-facing message when a flat-rate item's product was deleted/unpublished
 * while in the cart. Shared across all checkout surfaces so the copy stays
 * consistent.
 */
export const FLAT_RATE_ITEM_NOT_FOUND_MESSAGE =
  'This item may no longer be available — please remove it from your cart or contact support@gearfocus.com.';

export function useShoppingCart() {
  return useContext(ShoppingCartContext);
}

export function calculateShipping(
  product: ProductDocument,
  address: AddressDocument
) {
  return product?.shipping_costs?.find((s) => {
    if (address.state_province === 'AK') return s.code === 'AK' ? s : false;
    if (address.state_province === 'HI') return s.code === 'HI' ? s : false;
    if (address.state_province === 'PR') return s.code === 'PR' ? s : false;
    if (s.code === address.country_code) return s;
    if (s.code === 'EU' && europeCodes.includes(address.country_code)) return s;
    if (s.code === 'SAM' && southAmCodes.includes(address.country_code))
      return s;
    // BUG FIX (the 'OT' catch-all): Array.find treats this callback's return
    // value as a truthy/falsy PREDICATE. The old code returned `s.cost` here,
    // so a genuinely-free OT band (cost === 0) returned the number 0 → falsy →
    // find treated it as "no match" and skipped it → calculateShipping
    // returned undefined → the item was treated as unresolved/$0. Returning the
    // band OBJECT (always truthy) makes find select it, like every other branch.
    if (s.code === 'OT') return s;
    return false;
  });
}

function shippingSelectionComplete(
  cart: Cart,
  selectedRates: SelectedShippingRates
) {
  // every item in the cart must have a shipping rate selected
  return cart.items.every((item) => {
    if (item.is_flat_rate) return true;
    const found = selectedRates.get(item.seller_id)?.get(item.product_id);
    return !!found;
  });
}

const UPDATE_INTERVAL = 1000 * 3; // 3 seconds
const getDefaultCart = (uid?: string) => cartSchema.parse({ uid: uid ?? '' });

export function ShoppingCartProvider({ children }: ShoppingCartProviderProps) {
  const { getEffectivePriceForProduct, ready: storeSalesReady } = useStoreSales();
  const [storeSaleNotice, setStoreSalePriceNotice] = useState<{
    message: string; productIds: string[]; ownerUid: string | null;
  } | null>(null);
  const storeSalePriceNotice = storeSaleNotice?.message ?? null;
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [selectedShippingRates, setSelectedShippingRates] =
    useState<SelectedShippingRates>(new Map());
  const [shippingAddress, setShippingAddress] = useState<AddressDocument>();
  const [savedForLater, setSavedForLater] = useState<CartItem[]>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const stored = localStorage.getItem('gearfocus-saved-for-later');
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });
  const [taxInfo, setTaxInfo] = useState<{
    value?: number;
    itemValues?: Map<string, number>;
    isCalculating: boolean;
  }>({
    isCalculating: false,
  });
  const { showToast } = useToastContext();
  const { userDoc, user } = useAuth();
  const [initialCart, setInitialCart] = useState<Cart | null>(null);
  const [cart, setCart] = useState<Cart>(() => getDefaultCart(userDoc?.uid));
  const debouncedCart = useDebounce<Cart>(cart, UPDATE_INTERVAL);
  const [cartOpen, setCartOpen] = useState(false);
  // E3 (TASK-1323 §4): tracks whether a cart persist write is currently
  // in-flight, so the focus-clobber detector can record `flush_in_flight`.
  // Ref (not state) to avoid stale-closure reads inside the query onSuccess.
  const flushInFlightRef = React.useRef(false);
  const setCartMutation = useMutation({
    mutationFn: setCartState,
    onMutate: () => {
      flushInFlightRef.current = true;
    },
    onSuccess: (_, variables) => {
      setInitialCart(variables.newCartState);
    },
    onSettled: () => {
      flushInFlightRef.current = false;
    },
  });
  const [localCartItems, setLocalCartItems] = useState<CartItem[]>([]);

  // E3 (TASK-1323 §4) refs. The window-focus refetch's `onSuccess` closure can
  // capture a STALE `cart`/`initialCart` from an older render (the core M2
  // hazard, doc §5) — so the detector reads current state through refs updated
  // every render, never through the closure. `lastCartChangeAtRef` timestamps
  // the last time the `cart` STATE OBJECT changed identity — which includes
  // server-driven `setCart` and `calcCart` recomputes, not only local user
  // mutations. `ms_since_last_cart_change` is read against that, so treat it as
  // "time since any cart state change," not "time since the user's last add."
  const latestCartRef = React.useRef<Cart | null>(null);
  const latestInitialCartRef = React.useRef<Cart | null>(null);
  const lastCartChangeAtRef = React.useRef<number>(Date.now());

  /**
   * E1b (TASK-1323 QA) — cheap best-effort tag for WHICH mutation most
   * recently produced an empty-items raw cart, so a desync emit's
   * `mutation_source` can distinguish 'clear' (should never fire post-fix —
   * that IS the point of this fix) from 'remove' (the real 2026-07-15 bug
   * this detector exists to catch) from 'other' (cart hasn't gone through
   * either path). NOT a full mutation-tracking system: only clearCart,
   * decreaseCartQty, and removeFromCart — the setters that can produce
   * items:[] — update it, and only the moment matters, not full history.
   */
  const lastCartMutationRef = React.useRef<'clear' | 'remove' | 'other'>(
    'other'
  );

  /**
   * Synchronous mirror of localCartItems. React state is stale between two
   * calls in the same tick (no commit in between), so two guest adds fired
   * before a render would both read qty 0 and both emit a "success" toast even
   * though the second is stock-capped. This ref is re-synced on every commit
   * AND reserved synchronously inside addGuestCartItem, so a same-tick second
   * add sees the reserved qty and is correctly treated as blocked.
   */
  const localCartItemsRef = React.useRef<CartItem[]>([]);
  useEffect(() => {
    localCartItemsRef.current = localCartItems;
  }, [localCartItems]);

  /**
   * Product ids with an add currently in flight. Guards against the rapid-tap
   * over-add reproduced by the CEO on a slow network (GB #1323, 2026-07-15):
   * `increaseCartQty` awaits a product fetch that can take 10-15s, and without
   * this lock N rapid taps fire N concurrent adds, each incrementing qty
   * before realtime stock data arrives. Concurrent adds of the SAME product
   * collapse to one; sequential (intentional) re-adds still work because the
   * lock releases when the first add settles.
   */
  const addingProductIdsRef = React.useRef<Set<string>>(new Set());
  // Reactive mirror of the in-flight set, exposed via context so the UI can
  // disable destructive actions (X-remove / Save-for-Later) on an item that is
  // mid-add. The ref above stays the synchronous source of truth for the guard.
  const [addingProductIds, setAddingProductIds] = useState<Set<string>>(
    new Set()
  );

  /**
   * Fallback product cache for flat-rate shipping resolution.
   * Keyed by product_id. Populated when a flat-rate cart item's product is not
   * yet in realTimeProducts (e.g. on initial render before the Firestore
   * snapshot arrives). Prevents the silent $0-shipping bug for those items.
   */
  const [shippingFallbackProducts, setShippingFallbackProducts] = useState<
    Map<string, ProductDocument>
  >(new Map());

  /**
   * Track which product IDs we have already started fetching so we don't
   * issue duplicate in-flight requests.
   */
  const fetchingFlatRateIds = React.useRef<Set<string>>(new Set());

  /**
   * Monotonic id for tax requests. Makes calculateTax LATEST-WINS: each call
   * captures an id, and only the most-recent call is allowed to write taxInfo.
   * This makes it safe to fire a recompute (e.g. when flat-rate shipping
   * resolves) while an earlier, stale tax calc is still in-flight — the stale
   * result is discarded instead of clobbering the newer, correct one.
   */
  const taxRequestIdRef = React.useRef(0);

  /**
   * Track flat-rate product IDs whose fetch COMPLETED but returned null —
   * i.e. the product was deleted/unpublished while it sat in the cart.
   * This is a HARD, non-transient state (distinct from a thrown network error,
   * which stays retryable). We store these in state (not just a ref) so the
   * resolution memo recomputes when an id becomes "not found", and so the
   * UI can surface an actionable "item no longer available" message instead
   * of spinning on the transient "Calculating…" copy forever.
   */
  const [notFoundFlatRateIds, setNotFoundFlatRateIds] = useState<Set<string>>(
    new Set()
  );

  // Get product IDs from either authenticated cart or guest cart
  const productIds = useMemo(() => {
    if (userDoc && cart) {
      return cart.items.map(item => item.product_id);
    }
    if (!user && localCartItems.length > 0) {
      return localCartItems.map(item => item.product_id);
    }
    return [];
  }, [cart, userDoc, user, localCartItems]);

  // Use the calculated cart
  const { realTimeProducts } = useRealTimeProducts(productIds);

  // A saved-cart refetch can replace a repriced item without changing the
  // product subscription or the sale callback. Track the fields we reprice.
  const cartPricingKey = JSON.stringify((user ? cart.items : localCartItems).map((item) =>
    [item.product_id, item.product_cost, item.store_sale_id ?? null]));

  // Reprice only cart items that have, or now qualify for, a store sale.
  // Ordinary list-price carts retain their exact existing float values.
  // The server rechecks at pay time if this client is offline or stale.
  useEffect(() => {
    if (!storeSalesReady || !realTimeProducts.length) return;
    const byId = new Map(realTimeProducts.map((product) => [product.id, product]));
    const reprice = (items: CartItem[]): {
      items: CartItem[]; changed: boolean; notice: string | null; changedProductIds: string[];
    } => {
      let changed = false;
      let notice: string | null = null;
      const changedProductIds: string[] = [];
      const next = items.map((item) => {
        const product = byId.get(item.product_id);
        if (!product) return item;
        const price = getEffectivePriceForProduct(product);
        if (!price || (!price.applied && !item.store_sale_id)) return item;
        const nextCost = price.applied ? price.unitCents / 100 : product.price;
        const nextSaleId = price.applied?.saleId;
        if (item.product_cost === nextCost && item.store_sale_id === nextSaleId) return item;
        changed = true;
        if (item.product_cost !== nextCost) {
          changedProductIds.push(item.product_id);
          const started = !item.store_sale_id && !!nextSaleId;
          const ended = !!item.store_sale_id && !nextSaleId;
          const replaced = !!item.store_sale_id && !!nextSaleId && item.store_sale_id !== nextSaleId;
          // Price direction cannot tell us whether a sale started, ended,
          // was replaced, or kept running while the listing price changed.
          if (!notice) {
            const title = product.title || item.product_title;
            if (started) notice = storeSaleStartedNotice(title);
            else {
              // The utilities barrel initializes Firestore modules. Load
              // its formatter only when the notice needs a dollar amount.
              // eslint-disable-next-line @typescript-eslint/no-require-imports -- Avoid eager Firestore initialization by the utilities barrel.
              const newPrice = (require('@util/index') as typeof import('@util/index')).formatCurrency(nextCost);
              notice = ended ? storeSaleEndedNotice(title, newPrice)
                : (replaced ? STORE_SALE_COPY_C6A : STORE_SALE_COPY_C7A)
                  .replace('{item}', () => title).replace('{new price}', () => newPrice);
            }
          }
        }
        // Firestore does not accept undefined fields. Remove the old sale ID
        // completely when a sale ends or the kill switch is turned off.
        const { store_sale_id: _oldSaleId, ...withoutSaleId } = item;
        return nextSaleId
          ? { ...withoutSaleId, product_cost: nextCost, store_sale_id: nextSaleId }
          : { ...withoutSaleId, product_cost: nextCost };
      });
      if (changedProductIds.length > 1) {
        notice = STORE_SALE_COPY_C6B.replace('{N}', String(changedProductIds.length));
      }
      return { items: next, changed, notice, changedProductIds };
    };
    const current = user ? cart.items : localCartItems;
    const updated = reprice(current);
    if (!updated.changed) return;
    if (updated.notice) {
      setStoreSalePriceNotice({ message: updated.notice, productIds: updated.changedProductIds,
        ownerUid: user?.uid ?? null });
    }
    if (user) {
      setCart((previous) => {
        const latest = reprice(previous.items);
        return latest.changed ? { ...previous, items: latest.items } : previous;
      });
    } else {
      setLocalCartItems((previous) => {
        const latest = reprice(previous);
        return latest.changed ? latest.items : previous;
      });
    }
  // The functional setters still preserve concurrent edits. A price-key change
  // re-runs once; the next pass sees the expected price and stops.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [realTimeProducts, getEffectivePriceForProduct, storeSalesReady, user, cartPricingKey]);

  // A notice describes the current cart and account. Removal (including
  // save-for-later and quantity-to-zero) invalidates even a multi-line count.
  useEffect(() => {
    if (!storeSaleNotice) return;
    const items = user ? cart.items : localCartItems;
    if (storeSaleNotice.ownerUid !== (user?.uid ?? null) ||
        storeSaleNotice.productIds.some((id) => !items.some((item) => item.product_id === id))) {
      setStoreSalePriceNotice(null);
    }
  }, [storeSaleNotice, user, cart.items, localCartItems]);

  // E3 (TASK-1323 §4): keep the focus-clobber detector's refs current every
  // render, and a ref to `realTimeProducts` for the detector's realtime_status.
  latestCartRef.current = cart;
  latestInitialCartRef.current = initialCart;
  const latestRealtimeRef = React.useRef<ProductDocument[]>([]);
  latestRealtimeRef.current = realTimeProducts;

  /**
   * E3 (TASK-1323 QA) — shared clobber check for the window-focus refetch's
   * BLIND branches. The results-truthy branch below already instruments the
   * "server cart differs from a dirty local cart" clobber via
   * `detectFocusClobber`. The null-results branch (no server doc — either the
   * doc genuinely doesn't exist, or `getCartState` swallowed a read error into
   * `{results:null}`, see cart.service.ts) and the onError branch both wipe
   * the ENTIRE local cart via `setCart(getDefaultCart(...))` with NO
   * instrumentation at all — so a transient read failure on window focus can
   * silently empty a non-empty, dirty cart.
   *
   * Reuses `detectFocusClobber` against a synthetic empty server cart (the
   * exact cart these branches are about to write) so the "would this wipe
   * lose local content" predicate stays in one place. DETECT ONLY — the wipe
   * behavior itself is untouched. `server_cart` discriminates which blind
   * branch fired: 'missing' (null results) vs 'error' (onError).
   */
  const detectAndEmitFocusClobberAgainstEmpty = useCallback(
    (serverCart: 'missing' | 'error') => {
      try {
        const localCart = latestCartRef.current;
        const localInitial = latestInitialCartRef.current;
        if (!localCart || localCart.items.length === 0) return;
        const { clobber, itemsOnlyLocal } = detectFocusClobber({
          firestoreCart: getDefaultCart(userDoc?.uid),
          localCart,
          localInitial,
        });
        if (!clobber) return;
        const loadedIds = new Set(
          latestRealtimeRef.current.map((p) => p.id)
        );
        const realtimeStatus: 'live' | 'loading' = localCart.items.every(
          (i) => loadedIds.has(i.product_id)
        )
          ? 'live'
          : 'loading';
        emitCartTelemetry(
          CART_TELEMETRY_EVENTS.FOCUS_CLOBBER,
          {
            ...buildCartCommonContext({
              auth: userDoc?.uid ? 'user' : 'guest',
              cartSize: localCart.items.length,
              productMapSize: latestRealtimeRef.current.length,
              realtimeStatus,
              sellerCount: new Set(
                localCart.items.map((i) => i.seller_id).filter(Boolean)
              ).size,
            }),
            items_only_local: itemsOnlyLocal,
            ms_since_last_cart_change: Date.now() - lastCartChangeAtRef.current,
            flush_in_flight: flushInFlightRef.current,
            server_cart: serverCart,
          },
          { fingerprint: CART_TELEMETRY_FINGERPRINTS.FOCUS_CLOBBER }
        );
      } catch {
        // telemetry must never break the refetch path
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userDoc?.uid]
  );

  /**
   * E1b (TASK-1323 QA) — shared raw-cart-desync detector, called from BOTH the
   * debounced persist effect and `flushCart`. Before this fix, `flushCart`
   * persisted the raw cart WITHOUT running this check, and its `onSuccess`
   * set `initialCart` to the just-flushed cart — so if the debounced effect's
   * pass ran later on the same (now-committed) value, `shouldUpdateFirestore`
   * would be false and the desync would never be detected at all for anything
   * that went through flushCart (e.g. the cart→checkout transition). Extracted
   * so both call sites share one detection path instead of drifting.
   */
  const detectAndEmitRawCartDesync = useCallback(
    (cartToFlush: Cart) => {
      const desync = detectRawCartDesync({
        itemsLength: cartToFlush.items.length,
        productIdsLength: cartToFlush.product_ids?.length ?? 0,
        subtotal: cartToFlush.subtotal ?? 0,
      });
      if (!desync.desync) return;
      const loadedIds = new Set(latestRealtimeRef.current.map((p) => p.id));
      emitCartTelemetry(
        CART_TELEMETRY_EVENTS.DRAWER_EMPTY_MISMATCH,
        {
          ...buildCartCommonContext({
            auth: userDoc?.uid ? 'user' : 'guest',
            cartSize: cartToFlush.items.length,
            productMapSize: latestRealtimeRef.current.length,
            realtimeStatus:
              cartToFlush.items.length > 0 &&
              cartToFlush.items.every((i) => loadedIds.has(i.product_id))
                ? 'live'
                : 'loading',
            sellerCount: new Set(
              cartToFlush.items.map((i) => i.seller_id).filter(Boolean)
            ).size,
          }),
          source: 'raw_cart_flush',
          desync_fields: desync.desyncFields,
          product_ids_count: cartToFlush.product_ids?.length ?? 0,
          subtotal: cartToFlush.subtotal ?? 0,
          // Best-effort tag for which mutation produced the items:[] shape —
          // see lastCartMutationRef. Post-fix, 'clear' should never appear
          // here; if it ever does, that's a regression signal.
          mutation_source: lastCartMutationRef.current,
          // Persisted bad doc — no drawer render to self-heal against.
          healed: false,
          heal_ms: null,
        },
        { fingerprint: CART_TELEMETRY_FINGERPRINTS.DRAWER_EMPTY }
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userDoc?.uid]
  );

  useEffect(() => {
    lastCartChangeAtRef.current = Date.now();
  }, [cart]);

  // For guest users, store cart in localStorage
  useEffect(() => {
    if (!user) {
      const savedCart = localStorage.getItem('guestCart');
      if (savedCart) {
        try {
          const parsedCart = JSON.parse(savedCart);
          setLocalCartItems(parsedCart);
        } catch (error) {
          console.error('Error parsing saved cart', error);
        }
      }
    }
  }, [user]);

  // Save guest cart to localStorage when it changes
  useEffect(() => {
    if (!user && localCartItems.length > 0) {
      localStorage.setItem('guestCart', JSON.stringify(localCartItems));
    }
  }, [localCartItems, user]);

  /**
   * Fallback fetch for flat-rate shipping resolution.
   *
   * When a shippingAddress is present and a flat-rate cart item's product is
   * missing from both realTimeProducts and shippingFallbackProducts (and is
   * not already in-flight), fetch it via getProductById.  Fetches are fired
   * in parallel so a cart with multiple missing products resolves together.
   *
   * Guard against render loops: we only track stable product_id strings in
   * the dependency array and in the in-flight ref, so this effect is stable.
   */
  const activeCartItems = user ? cart.items : localCartItems;
  const flatRateProductIds = useMemo(
    () => activeCartItems.filter((i) => i.is_flat_rate).map((i) => i.product_id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(activeCartItems.filter((i) => i.is_flat_rate).map((i) => i.product_id))]
  );

  useEffect(() => {
    if (!shippingAddress) return;

    // Cleanup: drop any "not found" ids that are no longer flat-rate cart
    // items (e.g. the buyer removed them), so a future re-add can re-fetch.
    setNotFoundFlatRateIds((prev) => {
      if (prev.size === 0) return prev;
      const stillInCart = new Set(flatRateProductIds);
      let changed = false;
      const next = new Set<string>();
      prev.forEach((id) => {
        if (stillInCart.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });

    const realTimeIds = new Set(realTimeProducts.map((p) => p.id));
    const fallbackIds = new Set(shippingFallbackProducts.keys());

    const idsToFetch = flatRateProductIds.filter(
      (id) =>
        !realTimeIds.has(id) &&
        !fallbackIds.has(id) &&
        !fetchingFlatRateIds.current.has(id) &&
        // Already determined to be deleted/unpublished — do NOT re-fetch
        // (avoids wasted reads). It stays a hard "not found" state.
        !notFoundFlatRateIds.has(id)
    );

    if (idsToFetch.length === 0) return;

    // Mark all as in-flight before any await so concurrent renders don't
    // re-trigger duplicate fetches for the same id.
    idsToFetch.forEach((id) => fetchingFlatRateIds.current.add(id));

    // Fire in parallel. Each id settles individually and is ALWAYS removed
    // from the in-flight set afterward (H1 fix). Three outcomes:
    //   - product found  -> cached in shippingFallbackProducts (resolves)
    //   - returned null  -> product deleted/unpublished; recorded in
    //                       notFoundFlatRateIds (hard, non-transient, NOT
    //                       re-fetched) so the UI shows "item not found"
    //                       instead of spinning on "Calculating…" forever
    //   - threw (network)-> nothing recorded; the in-flight release below
    //                       lets a later render retry (kept retryable)
    idsToFetch.map(async (id) => {
      try {
        const product = await getProductById(id);
        if (product) {
          setShippingFallbackProducts((prev) => {
            if (prev.has(id)) return prev;
            const next = new Map(prev);
            next.set(id, product);
            return next;
          });
        } else {
          // Completed successfully but the product no longer exists.
          console.warn(
            '[ShoppingCartContext] fallback product fetch returned null (deleted/unpublished)',
            id
          );
          setNotFoundFlatRateIds((prev) => {
            if (prev.has(id)) return prev;
            const next = new Set(prev);
            next.add(id);
            return next;
          });
        }
      } catch (err) {
        // Network/transient error — leave retryable (do NOT mark not-found).
        console.error('[ShoppingCartContext] fallback product fetch failed', id, err);
      } finally {
        // Always release the in-flight lock. For successes the presence check
        // prevents re-fetch; for failures this allows a retry.
        fetchingFlatRateIds.current.delete(id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shippingAddress, flatRateProductIds, realTimeProducts, shippingFallbackProducts, notFoundFlatRateIds]);

  // #region DANGER ZONE
  useQuery({
    queryKey: ['cart', userDoc?.uid ?? ''],
    queryFn: () => getCartState({ uid: userDoc?.uid ?? '' }),
    enabled: !!userDoc?.uid, // disabled until the uid is available
    staleTime: Infinity, // stale time inifity to only fetch once
    refetchOnWindowFocus: 'always',
    onSuccess: async (data) => {
      if (data.results) {
        const firestoreCart = calcCart({ ...data.results });

        // E3 (TASK-1323 §4) — DETECT ONLY, no behavior change. A window-focus
        // refetch is about to overwrite local state; if local state is dirty
        // (has pending, un-persisted changes) this overwrite is the M2 clobber
        // that loses a just-added item. We read CURRENT state through refs (the
        // closure `cart`/`initialCart` may be stale — doc §5) and log; the
        // existing overwrite below is untouched. PR-4 flips this to also skip.
        try {
          const localCart = latestCartRef.current;
          const localInitial = latestInitialCartRef.current;
          if (localCart) {
            const { clobber, itemsOnlyLocal } = detectFocusClobber({
              firestoreCart,
              localCart,
              localInitial,
            });
            if (clobber) {
              const loadedIds = new Set(
                latestRealtimeRef.current.map((p) => p.id)
              );
              const realtimeStatus: 'live' | 'loading' =
                localCart.items.length > 0 &&
                localCart.items.every((i) => loadedIds.has(i.product_id))
                  ? 'live'
                  : 'loading';
              emitCartTelemetry(
                CART_TELEMETRY_EVENTS.FOCUS_CLOBBER,
                {
                  ...buildCartCommonContext({
                    auth: userDoc?.uid ? 'user' : 'guest',
                    cartSize: localCart.items.length,
                    productMapSize: latestRealtimeRef.current.length,
                    realtimeStatus,
                    sellerCount: new Set(
                      localCart.items.map((i) => i.seller_id).filter(Boolean)
                    ).size,
                  }),
                  items_only_local: itemsOnlyLocal,
                  ms_since_last_cart_change:
                    Date.now() - lastCartChangeAtRef.current,
                  flush_in_flight: flushInFlightRef.current,
                },
                { fingerprint: CART_TELEMETRY_FINGERPRINTS.FOCUS_CLOBBER }
              );
            }
          }
        } catch {
          // telemetry must never break the refetch path
        }

        if (isEqual(firestoreCart, cart)) return;
        setCart(firestoreCart);
        setInitialCart(firestoreCart);
      } else {
        // E3 (TASK-1323 QA) blind branch: no server cart doc — either it
        // genuinely doesn't exist, or getCartState swallowed a read error into
        // {results:null}. See detectAndEmitFocusClobberAgainstEmpty.
        detectAndEmitFocusClobberAgainstEmpty('missing');
        setCart(getDefaultCart(userDoc?.uid));
        setInitialCart(null);
      }
    },
    onError: () => {
      // E3 (TASK-1323 QA) blind branch: the refetch itself errored.
      detectAndEmitFocusClobberAgainstEmpty('error');
      setCart(getDefaultCart(userDoc?.uid));
      setInitialCart(null);
    },
  });

  const shouldUpdateFirestore = useMemo(() => {
    // compare initial cart and debounced cart, but exclude the last_updated values
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { last_updated: lastUpdatedDebounced, ...restDebounce } =
      debouncedCart;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { last_updated: lastUpdatedInitial, ...restInitial } =
      initialCart ?? {};
    return !isEqual(restDebounce, restInitial);
  }, [debouncedCart, initialCart]);

  useEffect(() => {
    if (!debouncedCart?.uid) {
      setCart(getDefaultCart(userDoc?.uid));
      return;
    }
    if (shouldUpdateFirestore) {
      // E1 desync variant (TASK-1323 §4) — DETECT ONLY. This is the real site
      // of Bill's 2026-07-15 repro: `removeFromCart`/`decreaseCartQty` return
      // `{ ...prev, items: filtered }` without re-running `calcCart`, so when the
      // last item is removed the RAW cart (and thus this persisted doc) can carry
      // `items: []` with stale non-empty `product_ids`/`subtotal`. Log it as the
      // doc is written; the write itself is unchanged. See detectAndEmitRawCartDesync.
      detectAndEmitRawCartDesync(debouncedCart);
      setCartMutation.mutateAsync({
        newCartState: { ...debouncedCart, last_updated: Date.now() },
      });
    }
    // this should only run after the debounce interval OR when the uid is updated,
    // don't fill out the dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedCart, userDoc?.uid]);

  /**
   * Bypass the 3s debounce. Used at the cart→checkout transition so the
   * persisted cart matches React state by the time other tabs / devices
   * read from Firestore. No-op for guests (no Firestore cart) and for
   * unchanged carts (matches the debounced effect's gate).
   */
  const flushCart = useCallback(async (): Promise<void> => {
    if (!userDoc?.uid || !cart?.uid) return;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { last_updated: lastUpdatedCart, ...restCart } = cart;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { last_updated: lastUpdatedInitial, ...restInitial } =
      initialCart ?? {};
    if (isEqual(restCart, restInitial)) return;
    // E1b parity fix (TASK-1323 QA): flushCart bypasses the debounced effect
    // entirely, so without this call a cart flushed here (e.g. at the
    // cart→checkout transition) was never checked for the raw-cart desync —
    // and flushCart's own onSuccess sets initialCart to the just-flushed
    // value, so a LATER debounced pass over the same content would also see
    // shouldUpdateFirestore=false and never detect it either.
    detectAndEmitRawCartDesync(cart);
    await setCartMutation.mutateAsync({
      newCartState: { ...cart, last_updated: Date.now() },
    });
  }, [cart, initialCart, userDoc?.uid, setCartMutation, detectAndEmitRawCartDesync]);

  // updates cart when shipping info changes
  useEffect(() => {
    const newCart = calcCart(cart);
    if (isEqual(newCart, cart)) return;
    setCart(newCart);

    if (
      !shippingAddress ||
      !newCart.items.length ||
      !shippingSelectionComplete(newCart, selectedShippingRates)
    ) {
      setTaxInfo({ isCalculating: false });
      return;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedShippingRates, shippingAddress, coupons]);

  useEffect(() => {
    const firestoreCart = calcCart({ ...cart });
    if (isEqual(firestoreCart, cart)) return;
    setCart(firestoreCart);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxInfo.value]);
  // #endregion DANGER ZONE

  // #region cart interactions
  function getItemCount() {
    let total = 0
    if (!user && localCartItems.length > 0) {
      // Guest carts are loaded straight from localStorage (JSON.parse, no
      // schema validation), so an item saved by an older version of the site
      // — or corrupted storage — can have a missing/null/non-numeric qty.
      // Fall back to 1 (a line item that exists represents at least one
      // unit) instead of letting a bad value poison the sum with NaN.
      total = localCartItems.reduce((accumulator, cartItem) => {
        const qty = Number(cartItem.qty);
        return accumulator + (Number.isFinite(qty) && qty > 0 ? qty : 1);
      }, 0)
    } else {
      total = cart.items.reduce(
        (accumulator, cartItem) => accumulator + cartItem.qty,
        0
      );
    }
    return total;
  }

  function getItemQty(id: string) {
    return cart.items.find((item) => item.product_id === id)?.qty || 0;
  }

  async function increaseCartQty(
    newItem: CartItem,
    dontOpenCart = false,
    stock?: number
  ): Promise<AddToCartResult> {
    // Rapid-tap guard (see addingProductIdsRef). A concurrent add for the same
    // product is already in flight — drop this call; its outcome is decided by
    // the in-flight one. Returning 'dropped' lets the drawer's undo keep its
    // affordance instead of clearing it for an add that didn't happen (F2).
    if (addingProductIdsRef.current.has(newItem.product_id)) return 'dropped';
    addingProductIdsRef.current.add(newItem.product_id);
    setAddingProductIds((prev) => new Set(prev).add(newItem.product_id));

    try {
      // GUEST branch (F3): the guest cart is client-side only, so there is no
      // fetch — mirror the rest of the guest path (client-side validation;
      // server validateOrder backstops at payment). Reuse addGuestCartItem's
      // synchronous ref reservation + stock cap so same-tick semantics match;
      // it also owns the success/blocked toasts and the E4 stock_cap emit
      // (auth 'guest'), and opens the cart on success. The in-flight lock +
      // pending state above are still set/cleared uniformly (in `finally`) so
      // the drawer disables destructive actions consistently for guests too.
      if (!user) {
        return addGuestCartItem(newItem, stock);
      }

      // AUTH branch. Capture at ENTRY whether this began as a BUMP (item already
      // in the cart). If a bump's item is removed / the cart cleared during the
      // fetch, the add must be DROPPED — never resurrected via the first-add
      // branch (GB #1323 F1: resurrection through a slow in-flight bump).
      const beganAsBump =
        latestCartRef.current?.items.some(
          (i) => i.product_id === newItem.product_id
        ) ?? false;
      // E4 (TASK-1323 §4) helper — makes add failures visible (GA4 always;
      // Sentry per the by-reason split below). ONE E4 per invocation: the first
      // reason wins and later calls are ignored, so a
      // single tap that both fails to fetch AND would hit a realtime stock cap
      // reports only `fetch_error` (the more fundamental failure), never a
      // double count. `extra` carries reason-specific fields (e.g. error_code).
      //
      // Idempotency also neutralizes React's StrictMode double-invoke of the
      // setCart updater (where the stock_cap emits live), which — being at
      // React's flush — runs AFTER `finally` releases the in-flight lock. The
      // lock still guarantees no CONCURRENT invocation for the same product can
      // start and interleave a second emit.
      let addFailedEmitted = false;
      const emitAddFailed = (
        reason: 'fetch_error' | 'deleted' | 'stock_cap',
        extra?: Record<string, unknown>
      ) => {
        if (addFailedEmitted) return;
        addFailedEmitted = true;
        const loadedIds = new Set(realTimeProducts.map((p) => p.id));
        const realtimeStatus: 'live' | 'loading' =
          cart.items.length > 0 &&
          cart.items.every((i) => loadedIds.has(i.product_id))
            ? 'live'
            : 'loading';
        // Sentry channel by reason:
        //   fetch_error — SILENT here. Its diagnosable Sentry event is the real
        //     captureException `logError` sends at the catch site (stack + code
        //     + `cause`); a captureMessage here would be a redundant second event.
        //   deleted / stock_cap — fingerprinted captureMessage (warning). They
        //     have NO logError anywhere, and the GA4 leg is not live yet (GTM
        //     pending), so without this they would be recorded nowhere.
        emitCartTelemetry(
          CART_TELEMETRY_EVENTS.ADD_FAILED,
          {
            ...buildCartCommonContext({
              auth: userDoc?.uid ? 'user' : 'guest',
              cartSize: cart.items.length,
              productMapSize: realTimeProducts.length,
              realtimeStatus,
              sellerCount: new Set(
                cart.items.map((i) => i.seller_id).filter(Boolean)
              ).size,
            }),
            reason,
            product_id: newItem.product_id,
            ...extra,
          },
          reason === 'fetch_error'
            ? undefined
            : {
                fingerprint: CART_TELEMETRY_FINGERPRINTS.ADD_FAILED,
                level: 'warning',
              }
        );
      };

      let productDoc: ProductDocument | null = null;
      let fetchThrew = false;
      try {
        productDoc = await getProductById(newItem.product_id);
      } catch (err) {
        // DEPENDENCY (F5): treating a throw as "transient, add optimistically"
        // is only safe because firestore.rules grants unconditional read on
        // /products/{id} — so a throw here is a network/transient failure, never
        // a permission-denied for a real-but-unreadable product. If product
        // reads are ever conditioned, route permission-denied to the
        // deleted-block path (like the productDoc === null case) instead.
        //
        // Network/transient fetch error. The buyer's intent is unambiguous, so
        // add optimistically and let realtime reconcile — NO rethrow (PR-1's
        // pre-hardening version rethrew and lost the add). Surface a SINGLE,
        // fully-diagnosable event: `logError` sends a real exception (code +
        // original error via `cause`) to Sentry + GCP, and the E4 GA4 event
        // carries error_code for slicing. The optimistic add below shows the
        // ItemAdded toast — or, if a realtime stockDoc already caps it, the
        // stock notice — never a contradictory error-then-success pair. logCart
        // no-ops on the null product, so an optimistic add fires no add_to_cart
        // analytics (an accepted undercount this very event makes visible).
        fetchThrew = true;
        const errorCode =
          typeof (err as { code?: unknown })?.code === 'string'
            ? (err as { code: string }).code
            : 'unknown';
        const wrapped = new Error(
          `[increaseCartQty] product fetch failed (code=${errorCode}, product_id=${newItem.product_id})`
        );
        (wrapped as Error & { cause?: unknown }).cause = err;
        logError(wrapped);
        emitAddFailed('fetch_error', { error_code: errorCode });
      }

      // Fetch SUCCEEDED but returned null: the product was deleted/unpublished
      // between page load and click. Distinct from a thrown error (retryable).
      // Emit (E4 deleted), then BLOCK — PR-2 flips PR-1's log-and-add-anyway to
      // a hard block so a dead product can never become a permanently-invisible
      // cart item.
      if (!fetchThrew && productDoc === null) {
        emitAddFailed('deleted');
        notifyUnavailable();
        return 'blocked';
      }

      if (productDoc?.out_of_stock) {
        emitAddFailed('stock_cap');
        notifyError(0);
        return 'blocked';
      }

      // Decide against the FRESHEST committed cart: a removeFromCart /
      // saveForLater / clearCart fired during the fetch has already committed,
      // so latestCartRef reflects it. Deciding here (not inside the setCart
      // updater) lets increaseCartQty return an accurate status AND moves the
      // toast/log side effects out of the updater (no StrictMode double-fire).
      const currentItems = latestCartRef.current?.items ?? [];
      const existing =
        currentItems.find((i) => i.product_id === newItem.product_id) ?? null;

      // F1: a bump whose item vanished during the fetch must NOT be resurrected
      // via the first-add branch (which would re-add it with a toast + analytics,
      // and the debounced flush would persist the ghost). Drop it: no state
      // change, no toast, no logCart.
      if (beganAsBump && existing == null) {
        return 'dropped';
      }

      // Prefer the just-fetched doc over the closure-captured realtime array:
      // productDoc was fetched DURING this add's RTT, so it's strictly fresher
      // than the realtime snapshot captured at click time (if stock dropped
      // 2→1 mid-fetch, the stale realtime entry would otherwise permit an
      // over-add). On the fetch-throw path productDoc is null and this falls
      // through to realtime. This is also what stops the slow-network over-add:
      // realTimeProducts is empty for a not-yet-subscribed item.
      const stockDoc =
        productDoc ??
        realTimeProducts.find((p) => p.id === newItem.product_id) ??
        undefined;

      if (existing == null) {
        // Genuine first add (did not begin as a bump).
        if (stockDoc && newItem.qty > stockDoc.stock) {
          emitAddFailed('stock_cap');
          notifyError(stockDoc.stock);
          return 'blocked';
        }
        setCart((currCart) => {
          // Re-guard at flush against a same-tick race: skip if it snuck in.
          if (currCart.items.some((i) => i.product_id === newItem.product_id)) {
            return currCart;
          }
          return calcCart({
            created: currCart.created,
            items: [...currCart.items, newItem],
            uid: currCart.uid,
          });
        });
        notifyItemsAdded();
        logCart(newItem, productDoc);
        if (!cartOpen && !dontOpenCart) setCartOpen(true);
        return 'added';
      }

      // Bump an item already in the cart, capped at available stock.
      if (stockDoc && existing.qty + newItem.qty > stockDoc.stock) {
        emitAddFailed('stock_cap');
        notifyError(stockDoc.stock);
        return 'blocked';
      }
      setCart((currCart) => {
        const cur = currCart.items.find(
          (i) => i.product_id === newItem.product_id
        );
        // Re-guard at flush: if the item vanished between decision and flush,
        // DROP rather than resurrect (F1) — never re-add via this branch.
        if (!cur) return currCart;
        // D3: re-check the cap against the CURRENT qty too, not just presence —
        // a server cart that batched in a higher qty between our decision and
        // this flush must not be pushed past stock client-side.
        if (stockDoc && cur.qty + newItem.qty > stockDoc.stock) {
          return currCart;
        }
        return calcCart({
          created: currCart.created,
          items: currCart.items.map((i) =>
            i.product_id === newItem.product_id
              ? { ...i, qty: i.qty + newItem.qty }
              : i
          ),
          uid: currCart.uid,
        });
      });
      notifyItemsAdded();
      logCart(newItem, productDoc);
      if (!cartOpen && !dontOpenCart) setCartOpen(true);
      return 'added';
    } finally {
      addingProductIdsRef.current.delete(newItem.product_id);
      setAddingProductIds((prev) => {
        const next = new Set(prev);
        next.delete(newItem.product_id);
        return next;
      });
    }
  }

  async function logCart(item: CartItem, product: ProductDocument | null) {
    try {
      if (product) {
        // for revenue roll
        const total =
          (item.shipping_cost || 0) + item.product_cost * (item.qty || 1);
        // logAddToCart fires fbq pixel; capture event_id so trackEvent's dataLayer push
        // carries the same id for Stape CAPI dedup
        logAddToCart(product, total, userDoc?.uid ?? '').then((eventId) => {
          trackEvent("add_to_cart", {
            currency: "USD",
            value: item.product_cost,
            items: formatCartItems([item]),
            event_id: eventId,
          }, getUserDataForDL(userDoc));
        });
      }
    } catch (e) {
      console.error(e);
    }
  }

  function decreaseCartQty(id: string) {
    if (user && userDoc) {
      // For authenticated users
      setCart((prev) => {
        const itemInCart = prev.items.find((item) => item.product_id === id);
        if (!itemInCart) return prev;

        trackRemoveFromCartEvent(itemInCart as CartItem);

        if (itemInCart.qty === 1) {
          // Best-effort tag for E1b's mutation_source (see lastCartMutationRef)
          // — this is the real 2026-07-15 desync repro path (last item removed).
          lastCartMutationRef.current = 'remove';
          return {
            ...prev,
            items: prev.items.filter((item) => item.product_id !== id),
          };
        }
        return {
          ...prev,
          items: prev.items.map((item) =>
            item.product_id === id ? { ...item, qty: item.qty - 1 } : item
          ),
        };
      });
    } else {
      // For guest users
      setLocalCartItems(prevItems => {
        const itemInCart = prevItems.find((item) => item.product_id === id);
        if (!itemInCart) return prevItems;

        trackRemoveFromCartEvent(itemInCart as CartItem);

        if (itemInCart.qty === 1) {
          return prevItems.filter((item) => item.product_id !== id);
        }
        return prevItems.map((item) =>
          item.product_id === id ? { ...item, qty: item.qty - 1 } : item
        );
      });
    }
  }

  function trackRemoveFromCartEvent(item: CartItem, includeQty = false) {
    if (!item) return;
    // Track the item being removed from the cart
    const itemToTrack = {
      ...item,
      qty: includeQty ? item.qty : 1,
    };

    trackEvent('remove_from_cart', {
      currency: 'USD',
      value: itemToTrack.product_cost * itemToTrack.qty,
      items: formatCartItems([itemToTrack]),
    });
  }

  function removeFromCart(id: string) {
    if (user && userDoc) {
      // For authenticated users
      setCart((prev) => {

        const itemToRemove = prev.items.find((item) => item.product_id === id);
        trackRemoveFromCartEvent(itemToRemove as CartItem, true);

        // Best-effort tag for E1b's mutation_source (see lastCartMutationRef)
        // — removing the last item is the real 2026-07-15 desync repro path.
        lastCartMutationRef.current = 'remove';
        return {
          ...prev,
          items: prev.items.filter((item) => item.product_id !== id),
        };
      });
    } else {
      // For guest users
      setLocalCartItems(prevItems => {

        const itemToRemove = prevItems.find((item) => item.product_id === id);
        trackRemoveFromCartEvent(itemToRemove as CartItem, true);

        return prevItems.filter((item) => item.product_id !== id)
      }
      );
    }
  }

  // V3: Save for later — moves item from cart to saved list
  function saveForLater(productId: string) {
    const item = cart.items.find((i) => i.product_id === productId);
    if (!item) return;
    setSavedForLater((prev) => {
      const updated = [...prev, item];
      try { localStorage.setItem('gearfocus-saved-for-later', JSON.stringify(updated)); } catch {}
      return updated;
    });
    removeFromCart(productId);
  }

  function addCoupons(coupons: Coupon[]) {
    setCoupons((p) => {
      const existingCouponIds = p.map((c) => c.id);
      const newCoupons = coupons.filter(
        (c) => !existingCouponIds.includes(c.id)
      );
      return [...p, ...newCoupons];
    });
  }

  function removeCoupon(coupon: Coupon) {
    setCoupons((p) => p.filter((c) => c.id !== coupon.id));
  }

  function handleShippingRateSelected(
    sellerId: string,
    productId: string,
    rate: Rate
  ) {
    setSelectedShippingRates((currRates) => {
      const existingSellerRates = currRates.get(sellerId);
      const newSellerRates = new Map(existingSellerRates);
      newSellerRates.set(productId, rate);
      return new Map(currRates.set(sellerId, newSellerRates));
    });
  }

  function updateShippingAddress(address: AddressDocument) {
    if (isEqual(address, shippingAddress)) return;
    setShippingAddress(address);
  }

  function clearCart() {
    if (user && userDoc) {
      // For authenticated users, use the API to clear cart.
      // BUG FIX (TASK-1323 QA): this used to spread-without-recompute
      // (`{ ...prev, items: [] }`), the same pattern as the remove path — it
      // left `product_ids`/`subtotal`/`total`/`shipping` stale-non-empty on
      // the RAW cart, which the debounced flush then persisted as a corrupt
      // doc AND the E1b desync detector correctly flagged on every completed
      // authenticated purchase (order-confirmation pages call clearCart on
      // checkout completion). Zero every derived field so the persisted doc
      // matches the truly-empty shape (mirrors getDefaultCart's empty cart;
      // `created`/`uid` are preserved from `prev` like before).
      lastCartMutationRef.current = 'clear';
      setCart((prev) => ({
        ...prev,
        items: [],
        product_ids: [],
        subtotal: 0,
        total: 0,
        shipping: 0,
      }));
      setInitialCart(null);
    } else {
      // Clear guest cart in localStorage
      setLocalCartItems([]);
      localStorage.removeItem('guestCart');
    }

    // Common cleanup regardless of user type
    setCoupons([]);
    setStoreSalePriceNotice(null);
    setSelectedShippingRates(new Map());
    setShippingAddress(undefined);
    setTaxInfo({ isCalculating: false });
  }

  function canAddProductToCart(productId: string) {
    const product = realTimeProducts.find((p) => p.id === productId);
    if (!product) return true; // if product is not found, assume it can be added
    const cartItem = cart.items.find((i) => i.product_id === productId);
    if (!cartItem) return true;
    return cartItem.qty < product.stock;
  }
  // #endregion cart interactions

  // #region helpers

  /**
   * Look up a flat-rate product in realTimeProducts first, then fall back to
   * the separately-fetched shippingFallbackProducts map.  Returns undefined
   * when the product is not yet available in either source.
   */
  const getFlatRateProduct = (productId: string): ProductDocument | undefined => {
    return (
      realTimeProducts.find((r) => r.id === productId) ??
      shippingFallbackProducts.get(productId)
    );
  };

  const calcFlatRateShipping = (
    productId: string,
    address: AddressDocument
  ) => {
    const foundProduct = getFlatRateProduct(productId);
    if (!foundProduct) return undefined;
    return calculateShipping(foundProduct, address);
  };

  /**
   * Returns shipping totals and an itemized map.
   *
   * Resolved means: product data found AND calculateShipping returned a
   * matching rate object.  A resolved cost of $0 is "genuinely free" — valid.
   * Unresolved means: product not yet available OR no region match.  These
   * are tracked in `unresolvedFlatRateIds` so the caller can gate checkout.
   */
  const getShipping = (
    items: CartItem[],
    shippingAddress?: AddressDocument
  ) => {
    let shippingTotal = 0;
    const itemizedMap = new Map<string, number>();
    const unresolvedFlatRateIds = new Set<string>();
    if (!shippingAddress) return { shippingTotal, itemizedMap, unresolvedFlatRateIds };
    items.forEach((i) => {
      if (i.is_flat_rate) {
        const flatRate = calcFlatRateShipping(i.product_id, shippingAddress!);
        if (flatRate === undefined) {
          // Product data not yet available OR no region match — mark unresolved.
          // Do NOT set a numeric value in itemizedMap; leaving it absent prevents
          // calcCart from coercing the unresolved state to $0.
          console.warn(
            '[ShoppingCartContext] getShipping: flat-rate product not yet resolved, marking unresolved.',
            i.product_id
          );
          unresolvedFlatRateIds.add(i.product_id);
          return;
        }
        // flatRate is a rate object (code + cost). A cost of 0 means genuinely free.
        shippingTotal += flatRate.cost ?? 0;
        itemizedMap.set(i.product_id, flatRate.cost ?? 0);
      } else {
        const selectedRate = selectedShippingRates
          .get(i.seller_id)
          ?.get(i.product_id);
        if (selectedRate) {
          shippingTotal += selectedRate.total_amount;
          itemizedMap.set(i.product_id, selectedRate.total_amount);
        }
      }
    });
    return { shippingTotal, itemizedMap, unresolvedFlatRateIds };
  };

  function getDiscount(total: number) {
    if (!coupons.length) return 0;
    // only support one coupon for now
    const coupon = coupons[0];
    if (coupon.amount_off !== null) {
      return coupon.amount_off;
    }
    if (coupon.percent_off !== null) {
      const percentOff = coupon.percent_off;
      return total * (percentOff / 100);
    }

    return 0;
  }

  function getCartItemsSubtotal(items: CartItem[]) {
    const total = items.reduce(
      (accumulator, cartItem) =>
        accumulator + cartItem.qty * cartItem.product_cost,
      0
    );
    const discount = getDiscount(total);
    return total - discount;
  }

  function calcCart({
    items,
    created,
    uid,
  }: {
    items: CartItem[];
    created: number;
    uid: string;
  }): Cart {
    const subtotal = getCartItemsSubtotal(items);
    const { shippingTotal, itemizedMap, unresolvedFlatRateIds } = getShipping(items, shippingAddress);
    // apply shipping cost to each item.
    // For unresolved flat-rate items, preserve the existing shipping_cost (if
    // any) rather than coercing to 0 — the old value might be the correct
    // resolved cost from a previous render.  Unresolved items are excluded from
    // itemizedMap so `?? i.shipping_cost` falls through to the stale value, and
    // if there is no stale value (brand-new add) we leave it undefined to make
    // the "unresolved" state explicit rather than silently showing $0.
    items = items.map((i) => {
      if (itemizedMap.has(i.product_id)) {
        // Resolved: use the freshly computed rate (includes genuinely-free $0).
        return { ...i, shipping_cost: itemizedMap.get(i.product_id) as number };
      }
      if (unresolvedFlatRateIds.has(i.product_id)) {
        // Unresolved flat-rate: keep shipping_cost as-is (do not write 0).
        return i;
      }
      // Carrier-rate or no-address item: same as before.
      return { ...i, shipping_cost: i.shipping_cost ?? 0 };
    });
    const product_ids = items.map((i) => i.product_id);
    const last_updated = Date.now();
    const total = subtotal + shippingTotal + (taxInfo?.value ?? 0);
    return {
      created,
      last_updated,
      product_ids,
      shipping: shippingTotal,
      subtotal,
      total,
      uid,
      items,
    };
  }

  async function calculateTax(
    items: CartItem[],
    shippingAddress: AddressDocument
  ) {
    // LATEST-WINS: capture this call's id before any async work. Only the most
    // recent call may write taxInfo; superseded calls return without writing.
    const requestId = ++taxRequestIdRef.current;
    setTaxInfo(() => ({ isCalculating: true }));
    try {
      const taxItems = items.map((i) => ({
        product_id: i.product_id,
        seller_id: i.seller_id,
        total: i.product_cost * i.qty,
        shipping: i.shipping_cost ?? 0,
      }));
      const res = await calculateTaxBatch(taxItems, shippingAddress);
      // A newer calculateTax superseded this one while it was in-flight —
      // discard this (stale) result so it can't clobber the newer one.
      if (requestId !== taxRequestIdRef.current) return;
      const itemValues = new Map<string, number>();
      let value = 0;
      res.forEach((r) => {
        value += r.amount;
        itemValues.set(r.product_id, r.amount);
      });
      setTaxInfo(() => ({ value, itemValues, isCalculating: false }));
    } catch (err) {
      console.error('Tax calculation failed:', err);
      // Same guard on the error path: a discarded stale call must not reset
      // taxInfo and clobber a newer in-flight calc's state.
      if (requestId !== taxRequestIdRef.current) return;
      setTaxInfo(() => ({ value: 0, itemValues: new Map(), isCalculating: false }));
    }
  }
  // #endregion helpers

  function notifyItemsAdded() {
    showToast(<ItemAddedToast />, {
      style: {
        borderRadius: '10px',
        backgroundColor: '#FDF4E9',
        borderStyle: 'solid',
        borderWidth: '1px',
        borderColor: '#C75008',
        width: '400px',
      },
    });
  }

  function notifyError(qty: number) {
    const message =
      qty === 0
        ? 'This item is out of stock.'
        : qty === 1
          ? 'There is only 1 available of this item.'
          : `There are only ${qty} available of this item.`;
    showToast(<ErrorToast message={message} />, {
      closeButton: false,
      style: {
        borderRadius: '10px',
        backgroundColor: '#FF5555',
        borderStyle: 'solid',
        borderWidth: '1px',
        borderColor: '#FF5555',
        width: '400px',
      },
    });
  }

  function notifyUnavailable() {
    showToast(<ErrorToast message="This listing is no longer available." />, {
      closeButton: false,
      style: {
        borderRadius: '10px',
        backgroundColor: '#FF5555',
        borderStyle: 'solid',
        borderWidth: '1px',
        borderColor: '#FF5555',
        width: '400px',
      },
    });
  }

  // Function to add items to guest cart
  function addGuestCartItem(
    newItem: CartItem,
    stock?: number
  ): AddToCartResult {
    if (user) return 'dropped'; // Only for guest users

    // Honor the requested quantity, not a hard-coded 1: the drawer's undo
    // re-adds the FULL removed quantity (snapshot.cartItem.qty), so a guest
    // undoing a qty-3 removal must get 3 back — matching the auth path, which
    // adds `newItem` with its own qty (D1).
    const addQty = newItem.qty > 0 ? newItem.qty : 1;

    // Client-side stock cap (server's validateOrder still backstops at
    // payment). Read the CURRENT stored qty from the synchronous ref — not
    // possibly-stale React state — so the blocked determination is correct even
    // for a second add fired in the same tick before a render commits. On
    // over-stock we BLOCK entirely (no partial add), matching the auth path.
    const currentItems = localCartItemsRef.current;
    const existingForCap = currentItems.find(
      (item) => item.product_id === newItem.product_id
    );
    if (
      stock !== undefined &&
      (existingForCap?.qty ?? 0) + addQty > stock
    ) {
      // Blocked: surface the cap and fire NO success side effects (toast /
      // add_to_cart analytics) — the add did not happen. But DO emit the E4
      // stock_cap failure so the metric covers guest surfaces too, not just the
      // authenticated increaseCartQty branches (guest surfaces that pass
      // product.stock are exactly the ones this would otherwise under-count).
      // Same shape as the auth helper: auth 'guest', with the Sentry fingerprint
      // per the by-reason split (stock_cap has no logError, so captureMessage is
      // its channel).
      const loadedIds = new Set(realTimeProducts.map((p) => p.id));
      const realtimeStatus: 'live' | 'loading' =
        currentItems.length > 0 &&
        currentItems.every((i) => loadedIds.has(i.product_id))
          ? 'live'
          : 'loading';
      emitCartTelemetry(
        CART_TELEMETRY_EVENTS.ADD_FAILED,
        {
          ...buildCartCommonContext({
            auth: 'guest',
            cartSize: currentItems.length,
            productMapSize: realTimeProducts.length,
            realtimeStatus,
            sellerCount: new Set(
              currentItems.map((i) => i.seller_id).filter(Boolean)
            ).size,
          }),
          reason: 'stock_cap',
          product_id: newItem.product_id,
        },
        { fingerprint: CART_TELEMETRY_FINGERPRINTS.ADD_FAILED, level: 'warning' }
      );
      notifyError(stock);
      return 'blocked';
    }

    // Reserve the increment synchronously so a same-tick second add sees it.
    localCartItemsRef.current = existingForCap
      ? currentItems.map((item) =>
          item.product_id === newItem.product_id
            ? { ...item, qty: item.qty + addQty }
            : item
        )
      : [...currentItems, { ...newItem, qty: addQty }];

    setLocalCartItems(prevItems => {
      const existingItem = prevItems.find(
        item => item.product_id === newItem.product_id
      );

      // Authoritative cap: never let concurrent rapid taps push qty past stock.
      if (stock !== undefined && (existingItem?.qty ?? 0) + addQty > stock) {
        return prevItems;
      }

      if (existingItem) {
        // If item exists, update quantity
        return prevItems.map(item =>
          item.product_id === newItem.product_id
            ? { ...item, qty: item.qty + addQty }
            : item
        );
      } else {
        // Add new item with the requested quantity
        return [...prevItems, { ...newItem, qty: addQty }];
      }
    });

    // Track add to cart for TIE/Revenue Roll (guest users)
    pushToRevRollLayer('rr_add_to_cart', {
      rr_cart_id: getTieCartId(),
      ecommerce: {
        currencyCode: 'USD',
        add: {
          products: [{
            name: newItem.product_title,
            id: newItem.product_id,
            image: newItem.product_image,
            price: newItem.product_cost,
            brand: newItem.product_brand,
            variant: '',
            quantity: addQty,
          }],
        },
      },
    });

    // Track add to cart for GA4
    trackEvent('add_to_cart', {
      currency: 'USD',
      value: newItem.product_cost,
      items: formatCartItems([newItem]),
    });

    // Show toast notification using the correct pattern
    showToast(<ItemAddedToast />, {
      style: {
        borderRadius: '10px',
        backgroundColor: '#FDF4E9',
        borderStyle: 'solid',
        borderWidth: '1px',
        borderColor: '#C75008',
        width: '400px',
      },
    });

    // Open cart by default unless specified
    setCartOpen(true);
    return 'added';
  }

  // Calculate the cart data including guest cart items.
  //
  // IMPORTANT: calcCart closes over selectedShippingRates, shippingAddress,
  // taxInfo, and coupons in addition to its explicit arguments. All of these
  // must appear in the dependency array so the memo recomputes when they change.
  // Omitting them caused stale cart totals (wrong shipping/tax amounts shown)
  // and in some cases an empty-items cart when realTimeProducts hadn't loaded yet.
  const calcedCart: Cart = useMemo(() => {
    // If user is logged in, use their stored cart
    if (userDoc && cart) {
      return calcCart({
        items: cart.items,
        created: cart.created,
        uid: userDoc.uid,
      });
    }

    // If guest user with items in localStorage
    if (!user && localCartItems.length > 0) {
      return calcCart({
        items: localCartItems,
        created: Date.now(),
        uid: 'guest',
      });
    }

    // Default empty cart
    return calcCart({
      items: [],
      created: Date.now(),
      uid: userDoc?.uid || 'guest',
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, userDoc, user, localCartItems, selectedShippingRates, shippingAddress, taxInfo, coupons, realTimeProducts, shippingFallbackProducts]);

  // Use the calculated cart
  const memoizedCart = useMemo(() => {
    return calcedCart;
  }, [calcedCart]);

  // This is the cart we expose to the application
  const cartToUse = memoizedCart;

  /**
   * Computes flat-rate shipping status, distinguishing THREE non-resolved
   * cases (H2 + the deleted-product dead-end fix):
   *   - TRANSIENT (loading): product not yet loaded → resolved=false,
   *     unavailable=undefined, notFound=false (UI shows "Calculating…")
   *   - REGION-UNAVAILABLE: product loaded but no rate for the buyer's region
   *     → resolved=false, unavailable={city,state} (UI: "seller may not ship
   *     here")
   *   - ITEM-NOT-FOUND: product was deleted/unpublished (fetch returned null)
   *     → resolved=false, notFound=true (UI: "this item may no longer be
   *     available — remove it or contact support"). This is HARD, never wedges
   *     on the transient copy, and submission is blocked (fail closed).
   *
   * Resolution requires: product data available (realTimeProducts or
   * shippingFallbackProducts) AND calculateShipping returned a matching rate.
   * A resolved cost of $0 is valid ("genuinely free").
   *
   * No shippingAddress yet → resolved=true (nothing to block on; an address is
   * required before payment is reachable).
   *
   * Precedence when multiple items are in different states: not-found and
   * region-unavailable (both hard) take priority over transient loading so the
   * buyer always sees an actionable message rather than a spinner.
   */
  const {
    flatRateShippingResolved,
    flatRateShippingUnavailable,
    flatRateShippingNotFound,
  } = useMemo<{
    flatRateShippingResolved: boolean;
    flatRateShippingUnavailable?: { city?: string; state?: string };
    flatRateShippingNotFound: boolean;
  }>(() => {
    if (!shippingAddress)
      return { flatRateShippingResolved: true, flatRateShippingNotFound: false };
    const flatRateItems = cartToUse.items.filter((i) => i.is_flat_rate);
    if (flatRateItems.length === 0)
      return { flatRateShippingResolved: true, flatRateShippingNotFound: false };

    let anyLoading = false;
    let anyUnavailable = false;
    let anyNotFound = false;
    for (const item of flatRateItems) {
      // Deleted/unpublished product takes priority — it can never resolve.
      if (notFoundFlatRateIds.has(item.product_id)) {
        anyNotFound = true;
        continue;
      }
      const product = getFlatRateProduct(item.product_id);
      if (!product) {
        anyLoading = true; // product data not yet available (transient)
        continue;
      }
      const rate = calculateShipping(product, shippingAddress);
      if (rate === undefined) {
        anyUnavailable = true; // no region match (permanent)
      }
    }

    if (anyNotFound) {
      // Hard: item no longer exists. Block checkout with an actionable message.
      return { flatRateShippingResolved: false, flatRateShippingNotFound: true };
    }
    if (anyUnavailable) {
      // Permanent: seller does not ship to this region. Report unavailable so
      // the UI can show an actionable message rather than a spinner forever.
      return {
        flatRateShippingResolved: false,
        flatRateShippingNotFound: false,
        flatRateShippingUnavailable: {
          city: shippingAddress.city_locality,
          state: shippingAddress.state_province,
        },
      };
    }
    if (anyLoading) {
      // Transient: still fetching product data. Show "Calculating…".
      return { flatRateShippingResolved: false, flatRateShippingNotFound: false };
    }
    return { flatRateShippingResolved: true, flatRateShippingNotFound: false };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartToUse.items, shippingAddress, realTimeProducts, shippingFallbackProducts, notFoundFlatRateIds]);

  // A store sale can start or end after tax was calculated at the shipping
  // step. Recalculate with the repriced cart line before checkout uses taxInfo.
  const saleTaxPricingKey = JSON.stringify(cartToUse.items.map((item) =>
    [item.product_id, item.product_cost, item.store_sale_id ?? null]));
  const previousSaleTaxPricesRef = React.useRef<Map<string, { cost: number; saleId?: string }>>(new Map());
  useEffect(() => {
    const previous = previousSaleTaxPricesRef.current;
    const next = new Map(cartToUse.items.map((item) => [item.product_id, {
      cost: item.product_cost, saleId: item.store_sale_id,
    }] as const));
    const salePriceChanged = cartToUse.items.some((item) => {
      const before = previous.get(item.product_id);
      return before && before.cost !== item.product_cost &&
        (before.saleId || item.store_sale_id);
    });
    previousSaleTaxPricesRef.current = next;
    if (salePriceChanged && shippingAddress && flatRateShippingResolved &&
      (taxInfo.value !== undefined || taxInfo.isCalculating)) {
      calculateTax(cartToUse.items, shippingAddress);
    }
    // Changes to taxInfo must not retrigger the calculation. Shipping's own
    // resolution effect below handles a sale change while a rate is pending.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saleTaxPricingKey]);

  // Recompute tax once when flat-rate shipping transitions unresolved → resolved.
  //
  // THE BUG (Codex P1): ShippingSection.handleContinue calls
  // calculateTax(cart.items, shippingAddress) at a point where a flat-rate
  // item may still be UNRESOLVED (item.shipping_cost === undefined). calculateTax
  // computes tax on `shipping: i.shipping_cost ?? 0`, i.e. $0 shipping. When the
  // fallback fetch later resolves and item.shipping_cost becomes the real cost,
  // nothing recomputes tax — so in states that tax shipping, tax is wrong, and
  // the order completes because the server trusts the client-submitted tax.
  //
  // FIX: when resolution flips false → true, recompute tax on the now-correct
  // cartToUse.items whenever tax has been requested — whether it already
  // finished (taxInfo.value !== undefined) OR is still in-flight on the stale
  // $0 shipping (taxInfo.isCalculating). The second case closes the race hole:
  // if we only checked taxInfo.value, a recompute would be skipped while the
  // first stale call is pending, and that stale result would then land
  // uncorrected. calculateTax is LATEST-WINS (taxRequestIdRef), so firing a
  // newer call here safely supersedes any in-flight stale one.
  //
  // Initial value `true` so this does NOT fire on mount, nor for carts that
  // start resolved (no flat-rate items, or shipping already known).
  const prevFlatRateResolvedRef = React.useRef(true);
  useEffect(() => {
    if (
      flatRateShippingResolved &&
      !prevFlatRateResolvedRef.current && // just transitioned false -> true
      shippingAddress &&
      cartToUse.items.length > 0 &&
      (taxInfo.value !== undefined || taxInfo.isCalculating) // tax requested: done OR pending on stale $0 shipping
    ) {
      // cartToUse.items now carry the resolved shipping_cost.
      calculateTax(cartToUse.items, shippingAddress);
    }
    prevFlatRateResolvedRef.current = flatRateShippingResolved;
    // Depend ONLY on flatRateShippingResolved. taxInfo and cartToUse.items are
    // read at call time, NOT in the deps, so calculateTax setting taxInfo can
    // never re-trigger this effect (no render loop). The ref guard also limits
    // this to at most once per unresolved→resolved transition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flatRateShippingResolved]);

  return (
    <ShoppingCartContext.Provider
      value={{
        addCoupons,
        addGuestCartItem,
        canAddProductToCart,
        cart: cartToUse,
        cartOpen,
        clearCart,
        coupons,
        decreaseCartQty,
        flushCart,
        getDiscount,
        getItemCount,
        getItemQty,
        handleShippingRateSelected,
        increaseCartQty,
        addingProductIds,
        realTimeProducts,
        storeSalePriceNotice,
        removeCoupon,
        removeFromCart,
        setCartOpen,
        shippingSelectionComplete: shippingSelectionComplete(
          cartToUse,
          selectedShippingRates
        ),
        selectedShippingRates,
        taxInfo,
        updateShippingAddress,
        calculateTax,
        shippingAddress,
        saveForLater,
        savedForLater,
        flatRateShippingResolved,
        flatRateShippingUnavailable,
        flatRateShippingNotFound,
      }}
    >
      <ShoppingCart />
      {children}
    </ShoppingCartContext.Provider>
  );
}
